// src/components/MarketInsights/utils/formatters.ts
import { parseCalendarDate } from "../../../lib/calendarDate";

/**
 * Format a number as currency (USD)
 */
export function formatCurrency(
  value: number | null | undefined,
  options?: { compact?: boolean; decimals?: number }
): string {
  if (value === null || value === undefined) return "No data yet";
  
  const { compact = false, decimals = 0 } = options || {};
  
  if (compact && value >= 1000) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(value);
  }
  
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

/**
 * Format a number with commas
 */
export function formatNumber(
  value: number | null | undefined,
  options?: { decimals?: number }
): string {
  if (value === null || value === undefined) return "No data yet";
  
  const { decimals = 0 } = options || {};
  
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

/**
 * Format square footage
 */
export function formatSqft(value: number | null | undefined): string {
  if (value === null || value === undefined) return "No data yet";
  return `${formatNumber(value)} sq ft`;
}

/**
 * Format price per square foot
 */
export function formatPricePerSqft(value: number | null | undefined): string {
  if (value === null || value === undefined) return "No data yet";
  return `${formatCurrency(value, { decimals: 2 })}/sq ft`;
}

/**
 * Format bedroom count
 */
export function formatBedrooms(bedrooms: number | null | undefined): string {
  if (bedrooms === null || bedrooms === undefined) return "No data yet";
  if (bedrooms === 0) return "Studio";
  if (bedrooms === 1) return "1 Bed";
  return `${bedrooms} Beds`;
}

/**
 * Format bathroom count
 */
export function formatBathrooms(bathrooms: number | null | undefined): string {
  if (bathrooms === null || bathrooms === undefined) return "No data yet";
  if (bathrooms === 1) return "1 Bath";
  return `${bathrooms} Baths`;
}

/**
 * Format bed/bath combo
 */
export function formatBedBath(
  bedrooms: number | null | undefined,
  bathrooms: number | null | undefined
): string {
  const beds = formatBedrooms(bedrooms);
  const baths = formatBathrooms(bathrooms);
  return `${beds} / ${baths}`;
}

/**
 * Format a date string
 */
export function formatDate(
  dateStr: string | null | undefined,
  options?: { relative?: boolean }
): string {
  if (!dateStr) return "Now";
  
  const date = parseCalendarDate(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const dateOnly = new Date(date);
  dateOnly.setHours(0, 0, 0, 0);
  
  // If date is today or in the past, show "Now"
  if (dateOnly <= today) return "Now";
  
  if (options?.relative) {
    const diffDays = Math.ceil(
      (dateOnly.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
    );
    
    if (diffDays === 1) return "Tomorrow";
    if (diffDays <= 7) return `In ${diffDays} days`;
    if (diffDays <= 30) {
      const weeks = Math.floor(diffDays / 7);
      return weeks === 1 ? "In 1 week" : `In ${weeks} weeks`;
    }
  }
  
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: date.getFullYear() !== today.getFullYear() ? "numeric" : undefined,
  });
}

/**
 * Format availability status
 */
export function formatAvailability(
  status: string,
  availableDate: string | null | undefined
): { text: string; color: "success" | "warning" | "error" | "default" } {
  if (status !== "available") {
    return { text: "Unavailable", color: "error" };
  }
  
  if (!availableDate) {
    return { text: "Available Now", color: "success" };
  }
  
  const date = new Date(availableDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  if (date <= today) {
    return { text: "Available Now", color: "success" };
  }
  
  return { text: `Available ${formatDate(availableDate)}`, color: "warning" };
}

/**
 * Format price range
 */
export function formatPriceRange(
  min: number | null | undefined,
  max: number | null | undefined
): string {
  if (min === null && max === null) return "Call for pricing";
  if (min === null) return `Up to ${formatCurrency(max)}`;
  if (max === null || min === max) return formatCurrency(min);
  return `${formatCurrency(min)} - ${formatCurrency(max)}`;
}

/**
 * Format percentage
 */
export function formatPercent(
  value: number | null | undefined,
  options?: { decimals?: number }
): string {
  if (value === null || value === undefined) return "No data yet";
  const { decimals = 1 } = options || {};
  return `${value.toFixed(decimals)}%`;
}

/**
 * Format rating (stars)
 */
export function formatRating(value: number | null | undefined): string {
  if (value === null || value === undefined) return "No rating";
  return `${value.toFixed(1)} ★`;
}

/**
 * Truncate text with ellipsis
 */
export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3) + "...";
}

/**
 * Get color for availability rate
 */
export function getAvailabilityColor(rate: number): "success" | "warning" | "error" {
  if (rate >= 70) return "success";
  if (rate >= 30) return "warning";
  return "error";
}

/**
 * Get color for price comparison
 */
export function getPriceComparisonColor(
  price: number,
  avgPrice: number
): "success" | "warning" | "error" {
  const diff = ((price - avgPrice) / avgPrice) * 100;
  if (diff <= -10) return "success"; // Below average
  if (diff >= 10) return "error"; // Above average
  return "warning"; // Near average
}
